<!--
  - Copyright (c) 2019. Botanická zahrada Přírodovědecké fakulty Univerzity Karlovy, Juraj Šimon
  -->

<template>
  <page>
    <template v-slot:content>
      <div class="page">
        <section class="page-section search-header">
          <div class="page-section-content">
            <div style="display: flex; flex-direction: column;">
              <div class="is-flex is-full-width">
                <div style="flex-grow: 1; position: relative;">
                  <div
                    v-if="search"
                    class="clear-icon"
                    title="Clear serach"
                    @click="clearSearch"
                  >
                    <ClearIcon width="24px" height="24px"></ClearIcon>
                  </div>
                  <div class="field">
                    <div class="control">
                      <input
                        @keydown="onSearchInputKeyUp"
                        autocomplete="off"
                        class="input is-primary search-input"
                        type="text"
                        placeholder="Type to search..."
                        v-model="search"
                      />
                    </div>
                  </div>
                </div>
                <search-button
                  :disabled="searchInProgress"
                  :loading="searchInProgress"
                  @click="runSearch"
                  :light="false"
                ></search-button>
              </div>
            </div>
            <div class="is-full-width" style="margin-top: 1rem;">
              Searching in: plants
            </div>
          </div>
        </section>
        <section class="page-section search-header">
          <div class="section-heading title is-size-4">
            {{
              searchResults
                ? `${totalCount} ${$tc("item found", totalCount)}`
                : "Search results"
            }}
          </div>
          <div class="page-section-content is-flex search-results">
            <pagination
              :page="page"
              :pages="pages"
              @prev="prevPage"
              @next="nextPage"
            ></pagination>
            <div
              v-if="searchInProgress && !searchResults"
              key="search-in-progress"
              class="is-flex"
              style="flex-direction: column;align-items: center;"
            >
              <div class="is-flex">
                <div style="width:24px; height: 24px; margin-right: .5rem;">
                  <Spinner></Spinner>
                </div>
                <span>Searching...</span>
              </div>
            </div>
            <div
              key="no-items-found"
              v-if="
                !searchInProgress && (!searchResults || !searchResults.length)
              "
              class="is-flex"
              style="flex-direction: column;align-items: center;"
            >
              <div style="font-size: 10rem;opacity: .7;font-weight: bold;">
                :(
              </div>
              <br />
              <div>Found items: 0</div>
            </div>
            <div class="results-list">
              <div
                class="results-list-item"
                :key="`item_${idx}`"
                v-for="(item, idx) in searchResults"
                :class="{ odd: idx % 2 }"
              >
                <result-item
                  :item="item"
                  :search="search"
                  :webp="webp"
                ></result-item>
              </div>
            </div>
            <pagination
              :page="page"
              :pages="pages"
              @prev="prevPage"
              @next="nextPage"
            ></pagination>
          </div>
        </section>
        <br />
        <br />
      </div>
    </template>
  </page>
</template>

<script>
import Page from "./../common/page";
import SearchButton from "./../common/search-button";
import ClearIcon from "./../../assets/img/clear-24px.svg";
import Spinner from "./../../components/common/spinner";
import { searchPlants } from "@/services/search";
import Pagination from "@/components/pages/search/pagination";
import ResultItem from "@/components/pages/search/result-item";
import { mapGetters } from "vuex";

let searchTimer;

export default {
  components: {
    Pagination,
    Page,
    ResultItem,
    SearchButton,
    ClearIcon,
    Spinner
  },
  data() {
    return {
      search: this.$route.query.q || "",
      searchInProgress: false,
      searchResults: null,
      totalCount: null,
      error: null,
      page: 1,
      rowsPerPage: 20
    };
  },
  computed: {
    pages() {
      if (this.totalCount) {
        return Math.ceil(this.totalCount / this.rowsPerPage);
      }
      return 1;
    },
    ...mapGetters({ webp: "settings/webp" })
  },
  metaInfo: {
    title: "Search"
  },
  destroyed() {
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
  },
  mounted() {
    this.doSearch();
  },
  beforeRouteEnter(from, to, next) {
    next(vm => {
      if (vm.search) {
        vm.doSearch();
      }
    });
  },
  methods: {
    runSearch() {
      this.page = 1;
      this.doSearch();
    },
    async doSearch() {
      if (this.$route.query.q !== this.search) {
        await this.$router.replace({
          query: Object.assign({}, this.$route.query, { q: this.search })
        });
      }
      this.error = null;
      this.searchInProgress = true;
      try {
        let { count, rows } = await searchPlants({
          search: this.search,
          page: this.page,
          rowsPerPage: this.rowsPerPage
        });
        this.searchResults = rows;
        this.totalCount = count;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Unable to perform search ", e);
        this.error = true;
      } finally {
        this.searchInProgress = false;
      }
    },
    onSearchInputKeyUp(evt) {
      if (evt.keyCode === 13) {
        this.doSearch();
        return;
      }
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
      searchTimer = setTimeout(() => {
        this.page = 1;
        this.doSearch();
      }, 500);
    },
    prevPage() {
      if (this.page > 1) {
        this.page--;
      }
      setTimeout(() => {
        this.doSearch();
      }, 10);
    },
    nextPage() {
      if (this.page < this.pages) {
        this.page++;
      }
      setTimeout(() => {
        this.doSearch();
      }, 10);
    },
    clearSearch() {
      this.search = "";
      if (this.$route.query.q) {
        this.$router.replace({
          query: Object.assign({}, this.$route.query, { q: null })
        });
      }
    }
  }
};
</script>
<style lang="scss" scoped>
@import "./../../styles/index";
@import "src/styles/clear-icon";
@import "./page-scoped";

.search-results {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.results-list {
  display: flex;
  flex-direction: column;
  width: 100%;

  &-item {
    width: 100%;
    padding: 0.5rem;
    background-color: white;
    border-top: 1px solid #cdcdcd;

    &.odd {
      background-color: #f9f9f9;
    }
  }
}

.search-header {
  margin-top: 2rem;
}
</style>
